import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Power, Smartphone, Globe, History, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Order the groups the way Frank approved the delivery tiers.
const GROUP_ORDER = ['tier1', 'tier2', 'tier3'];

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/**
 * App Features — the kill switches for every merchant function, per channel.
 *
 * Flipping a switch calls PUT /api/admin/features; the backend refuses the
 * matching money routes within ~5 s (feature-gate middleware), the Connect
 * apps hide the entry on their next flag refresh, and the merchant web menu
 * hides it on reload. "Apps" is Mojo Connect on Android and iOS; "Website" is
 * the merchant portal here.
 */
export const AppFeaturesTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(null); // { key, channel, enabled, label }

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/features`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load feature switches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const master = useMemo(() => data?.features?.find((f) => f.key === data.master_key), [data]);
  const groups = useMemo(() => {
    const byGroup = {};
    (data?.features || []).forEach((f) => {
      if (f.key === data.master_key) return;
      (byGroup[f.group] = byGroup[f.group] || { label: f.group_label, items: [] }).items.push(f);
    });
    return GROUP_ORDER.filter((g) => byGroup[g]).map((g) => ({ key: g, ...byGroup[g] }));
  }, [data]);

  const apply = async (key, channel, enabled) => {
    const field = channel === 'app' ? 'app_enabled' : 'web_enabled';
    setBusyKey(`${key}:${channel}`);
    // Optimistic: the switch moves at once, and snaps back on failure.
    setData((d) => ({
      ...d,
      features: d.features.map((f) => (f.key === key ? { ...f, [field]: enabled } : f)),
    }));
    try {
      const res = await axios.put(`${API}/admin/features`, {
        changes: { [key]: { [field]: enabled } },
        note: note.trim() || null,
      }, { headers });
      setData(res.data);
      // The reason belongs to the change it was typed for; left in place it
      // would be filed against every later flip too.
      setNote('');
      const label = res.data.features.find((f) => f.key === key)?.label || key;
      const where = channel === 'app' ? 'the apps' : 'the website';
      if (enabled) toast.success(`${label} is back on for ${where}`);
      else toast.warning(`${label} switched OFF for ${where}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Change not saved');
      await load();
    } finally {
      setBusyKey(null);
    }
  };

  const requestChange = (f, channel, enabled) => {
    // Turning something OFF (or anything on the master row) asks first;
    // turning a single feature back on just happens.
    if (!enabled || f.key === data.master_key) {
      setConfirm({ key: f.key, channel, enabled, label: f.label, isMaster: f.key === data.master_key });
    } else {
      apply(f.key, channel, enabled);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#F2B10C]" />
      </div>
    );
  }
  if (!data) {
    return (
      <Card><CardContent className="py-10 text-center text-gray-600">
        Feature switches are not available. Has db_scripts/021_feature_flags.sql been applied?
        <div className="mt-4"><Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-2" />Retry</Button></div>
      </CardContent></Card>
    );
  }

  const appMasterOff = master && !master.app_enabled;
  const webMasterOff = master && !master.web_enabled;

  return (
    <div className="space-y-6" data-testid="app-features-tab">
      {/* Master switch */}
      <Card className={`border-2 ${appMasterOff || webMasterOff ? 'border-red-400 bg-red-50/40' : 'border-[#F2B10C]/40'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-[#1B0C34] flex items-center gap-2">
                <Power size={18} className={appMasterOff || webMasterOff ? 'text-red-600' : 'text-[#145539]'} />
                {master?.label || 'All merchant features'}
              </CardTitle>
              <CardDescription className="mt-1">{master?.description}</CardDescription>
              {master?.updated_by && (
                <p className="text-xs text-gray-500 mt-2">Last changed {fmtWhen(master.updated_at)} by {master.updated_by}{master.note ? ` — "${master.note}"` : ''}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <ChannelSwitch icon={<Smartphone size={14} />} label="Apps" checked={!!master?.app_enabled}
                busy={busyKey === `${master?.key}:app`} onChange={(v) => requestChange(master, 'app', v)} big />
              <ChannelSwitch icon={<Globe size={14} />} label="Website" checked={!!master?.web_enabled}
                busy={busyKey === `${master?.key}:web`} onChange={(v) => requestChange(master, 'web', v)} big />
            </div>
          </div>
        </CardHeader>
        {(appMasterOff || webMasterOff) && (
          <CardContent className="pt-0">
            <p className="text-sm text-red-700 font-medium">
              Every merchant function is OFF for {appMasterOff && webMasterOff ? 'the apps and the website' : appMasterOff ? 'the apps' : 'the website'}.
              Merchants see "temporarily unavailable" there until this is switched back on.
            </p>
          </CardContent>
        )}
      </Card>

      {/* Reason for the next change */}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <Label htmlFor="ff-note" className="text-sm font-medium">Reason for the next change (optional)</Label>
          <Input id="ff-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={255}
            placeholder="e.g. Postbank UAT tunnel down since 14:00" className="mt-1" />
        </div>
        <p className="text-xs text-gray-500 sm:pb-2">Recorded against each flip in the history below.</p>
      </div>

      {/* Groups */}
      {groups.map((g) => (
        <Card key={g.key}>
          <CardHeader className="pb-2">
            <CardTitle className="text-[#1B0C34] text-base">{g.label}</CardTitle>
            <CardDescription>
              <span className="inline-flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1"><Smartphone size={12} /> Apps = Mojo Connect (Android &amp; iOS)</span>
                <span className="inline-flex items-center gap-1"><Globe size={12} /> Website = merchant portal</span>
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="divide-y">
            {g.items.map((f) => {
              const effApp = f.app_enabled && !appMasterOff;
              const effWeb = f.web_enabled && !webMasterOff;
              return (
                <div key={f.key} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`feature-${f.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#1B0C34]">{f.label}</span>
                      {!effApp && <Badge variant="destructive" className="text-[10px]">OFF in apps</Badge>}
                      {!effWeb && <Badge variant="destructive" className="text-[10px]">OFF on website</Badge>}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{f.description}</p>
                    {f.routes?.length > 0 && (
                      <p className="text-[11px] text-gray-400 mt-1 truncate" title={f.routes.join('  ')}>
                        Blocks: {f.routes.join(', ')}
                      </p>
                    )}
                    {f.updated_by && f.updated_by !== 'system' && (
                      <p className="text-[11px] text-gray-500 mt-1">Changed {fmtWhen(f.updated_at)} by {f.updated_by}{f.note ? ` — "${f.note}"` : ''}</p>
                    )}
                  </div>
                  <div className="flex gap-5 shrink-0">
                    <ChannelSwitch icon={<Smartphone size={14} />} label="Apps" checked={!!f.app_enabled} dimmed={appMasterOff}
                      busy={busyKey === `${f.key}:app`} onChange={(v) => requestChange(f, 'app', v)} />
                    <ChannelSwitch icon={<Globe size={14} />} label="Website" checked={!!f.web_enabled} dimmed={webMasterOff}
                      busy={busyKey === `${f.key}:web`} onChange={(v) => requestChange(f, 'web', v)} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* History */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-[#1B0C34] text-base flex items-center gap-2"><History size={16} /> Recent changes</CardTitle>
          <CardDescription>The last 50 flips, newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {(data.history || []).length === 0 ? (
            <p className="text-sm text-gray-500">No changes yet — everything is on.</p>
          ) : (
            <ul className="text-sm divide-y">
              {data.history.map((h, i) => (
                <li key={i} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-gray-500 text-xs w-28 shrink-0">{fmtWhen(h.changed_at)}</span>
                  <span className="font-medium">{h.label}</span>
                  <span className="text-xs inline-flex items-center gap-1 text-gray-600">{h.channel === 'app' ? <Smartphone size={12} /> : <Globe size={12} />}{h.channel === 'app' ? 'apps' : 'website'}</span>
                  <Badge variant={h.enabled ? 'secondary' : 'destructive'} className="text-[10px]">{h.enabled ? 'ON' : 'OFF'}</Badge>
                  <span className="text-xs text-gray-500">by {h.changed_by}</span>
                  {h.note && <span className="text-xs text-gray-500 italic">“{h.note}”</span>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Confirm turning something off */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.enabled ? 'Switch back on' : 'Switch off'} {confirm?.isMaster ? 'ALL merchant features' : confirm?.label} for {confirm?.channel === 'app' ? 'the apps' : 'the website'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.enabled
                ? 'Merchants get this back within a few seconds on the backend, and on their next screen refresh in the apps.'
                : confirm?.isMaster
                  ? 'Every merchant function stops working there within a few seconds. Merchants see "temporarily unavailable". Customers are not affected.'
                  : 'Requests for this function are refused within a few seconds and the entry is hidden in the apps on their next refresh. Customers are not affected.'}
              {note.trim() && <><br /><br />Reason: “{note.trim()}”</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirm?.enabled ? 'bg-[#145539] hover:bg-[#145539]/90' : 'bg-red-600 hover:bg-red-700'}
              onClick={() => { const c = confirm; setConfirm(null); if (c) apply(c.key, c.channel, c.enabled); }}
            >
              {confirm?.enabled ? 'Switch on' : 'Switch off'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ChannelSwitch = ({ icon, label, checked, onChange, busy, dimmed, big }) => (
  <label className={`flex items-center gap-2 ${dimmed ? 'opacity-50' : ''}`} title={dimmed ? 'Master switch is off for this channel' : undefined}>
    <span className={`inline-flex items-center gap-1 ${big ? 'text-sm font-medium' : 'text-xs'} text-gray-700 w-16`}>{icon}{label}</span>
    {busy
      ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      : <Switch checked={checked} onCheckedChange={onChange}
          className={checked ? 'data-[state=checked]:bg-[#145539]' : 'data-[state=unchecked]:bg-red-400'} />}
  </label>
);

export default AppFeaturesTab;
